<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Loading State -->
    <Loading v-if="loading" :text="$t('common.loading')" />

    <!-- Group list -->
    <div
      v-else-if="groupsWithGoatCount.length > 0"
      class="flex-1 flex flex-col gap-3 container-padding pb-24 overflow-y-auto">
      <!-- All Goats Option -->
      <ModuleShell
        :title="$t('views.session.allGoats')"
        :icon="MapPinHouse"
        :margin="false"
        @click="selectAllGoats">
        <div class="flex justify-between items-center">
          <p class="text-sm text-[var(--color-text)]">
            {{ $t('views.session.viewAllGoats') }}
          </p>
          <span class="text-sm text-[var(--color-primary-700)] font-medium">
            {{ goats.length }}
            {{
              goats.length === 1
                ? $t('views.session.goat')
                : $t('views.session.goats')
            }}
          </span>
        </div>
      </ModuleShell>

      <!-- Ungrouped Goats Option -->
      <ModuleShell
        :title="$t('views.session.ungroupedGoats')"
        :icon="MapPinHouse"
        :margin="false"
        @click="selectUngroupedGoats">
        <div class="flex justify-between items-center">
          <p class="text-sm text-[var(--color-text)]">
            {{ $t('views.session.viewUngroupedGoats') }}
          </p>
          <span class="text-sm text-[var(--color-primary-700)] font-medium">
            {{ ungroupedCount }}
            {{
              ungroupedCount === 1
                ? $t('views.session.goat')
                : $t('views.session.goats')
            }}
          </span>
        </div>
      </ModuleShell>

      <!-- Divider -->
      <div class="border-t border-[var(--color-border)] my-2"></div>

      <!-- Groups -->
      <ModuleShell
        v-for="group in groupsWithGoatCount"
        :key="group.id"
        :title="group.name"
        :icon="MapPinHouse"
        :margin="false"
        @click="selectGroup(group)">
        <div class="flex justify-between items-center">
          <p class="text-sm text-[var(--color-text)]">{{ group.location }}</p>
          <span class="text-sm text-[var(--color-primary-700)] font-medium">
            {{ group.goatCount }}
            {{
              group.goatCount === 1
                ? $t('views.session.goat')
                : $t('views.session.goats')
            }}
          </span>
        </div>
      </ModuleShell>
    </div>
    <div v-else class="flex-1 flex items-center justify-center px-4">
      <div class="text-center">
        <div class="text-6xl mb-4"></div>

        <h2 class="text-xl font-bold mb-2">
          {{ $t('views.goats.noGoatsYet') }}
        </h2>

        <p class="text-[var(--color-text)] mb-4">
          {{ $t('views.goats.startCreating') }}
        </p>

        <button
          @click="goToNewGoat"
          class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[var(--color-primary-200)] text-[var(--color-primary-900)] rounded-lg border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)] transition-colors duration-200 font-semibold">
          <PlusCircle class="w-5 h-5" />
          {{ $t('views.goats.newGoat') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import BackButton from '@/components/ui/buttons/GetBackButton.vue'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
  import Loading from '@/components/ui/feedback/Loading.vue'
  import { useRouter, useRoute } from 'vue-router'
  import { MapPinHouse, PlusCircle } from 'lucide-vue-next'
  import { onMounted, ref, computed } from 'vue'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import { useUiStore } from '@/stores/ui.store.js'

  const router = useRouter()
  const route = useRoute()

  const { t } = useI18n()
  const uiStore = useUiStore()
  const groupStore = useGroupStore()
  const goatStore = useGoatStore()

  // Get reactive references to groups and goats
  const { groupsWithGoatCount } = storeToRefs(groupStore)
  const { goats } = storeToRefs(goatStore)

  // Loading state
  const loading = ref(true)

  // Compute ungrouped goat count
  const ungroupedCount = computed(() => {
    return goats.value.filter((goat) => !goat.groupId).length
  })

  onMounted(() => {
    uiStore.setHeader({
      title: t('views.session.selectGroup'),
      showBack: true,
      showAdd: false,
    })
  })

  onMounted(async () => {
    loading.value = true

    try {
      // Fetch groups with cached data optimization
      if (groupsWithGoatCount.value.length === 0) {
        await groupStore.fetchGroups()
      }

      // Fetch goats to calculate counts and check if redirect needed
      if (goats.value.length === 0) {
        await goatStore.fetchGoats()
      }

      // Auto-redirect: Skip this view if no groups exist
      // Use router.replace to prevent this view from appearing in browser history
      if (groupsWithGoatCount.value.length === 0 && goats.value.length > 0) {
        router.replace(`/session/${route.params.type}/all/goats`)
      }
    } catch (error) {
      console.error('Failed to load groups/goats:', error)
    } finally {
      loading.value = false
    }
  })

  // Navigate to goat selection for the selected group
  function selectGroup(group) {
    router.push(`/session/${route.params.type}/${group.id}/goats`)
  }

  // Navigate to all goats
  function selectAllGoats() {
    router.push(`/session/${route.params.type}/all/goats`)
  }

  // Navigate to ungrouped goats
  function selectUngroupedGoats() {
    router.push(`/session/${route.params.type}/ungrouped/goats`)
  }

  function goToNewGoat() {
    router.push({ name: 'NewGoat' })
  }
</script>
